import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPause, faPlay, faStop, faVolumeHigh } from "@fortawesome/free-solid-svg-icons";
import * as analytics from "../../utils/analytics";
import { isSpeechSupported } from "../../utils/speak";
import {
    createReadAloudPlayer,
    hasJapaneseVoice,
    type ReadAloudPlayer,
    type ReadAloudProgress,
    type ReadAloudSegment,
} from "../../utils/readAloud";
import styles from "./ReadAloudControls.module.scss";

type PlayState = "idle" | "playing" | "paused";

interface ReadAloudControlsProps {
    segments: ReadAloudSegment[];
    /** Blog post slug; analytics is only tracked when given. */
    slug?: string;
    /** Overrides the blog "Listen to this post" heading. */
    title?: string;
    /** Speech rate (1 = normal). */
    rate?: number;
    /** Hide the "Section X of Y" line when the caller highlights the text itself. */
    showProgress?: boolean;
    /** Index of the block (as in segments[].blockIndex) being spoken, or null when idle. */
    onBlockChange?: (blockIndex: number | null) => void;
}

export default function ReadAloudControls({ segments, slug, title, rate, showProgress = true, onBlockChange }: ReadAloudControlsProps) {
    const { t } = useTranslation("blogPage");
    const [state, setState] = useState<PlayState>("idle");
    const [progress, setProgress] = useState<ReadAloudProgress | null>(null);
    const [failed, setFailed] = useState(false);
    const [missingJapaneseVoice, setMissingJapaneseVoice] = useState(false);
    const playerRef = useRef<ReadAloudPlayer | null>(null);
    const supported = useMemo(() => isSpeechSupported(), []);
    const needsJapanese = useMemo(() => segments.some((segment) => segment.lang === "ja-JP"), [segments]);

    const reset = useCallback(() => {
        setState("idle");
        setProgress(null);
    }, []);

    // Speech must never outlive the post it belongs to.
    useEffect(() => {
        playerRef.current?.stop();
        playerRef.current = null;
        reset();
        setFailed(false);
        return () => {
            playerRef.current?.stop();
            playerRef.current = null;
        };
    }, [segments, reset]);

    // Voices load asynchronously in Chrome, so re-check when they arrive.
    useEffect(() => {
        if (!supported || !needsJapanese || state === "idle") return undefined;
        const check = () => setMissingJapaneseVoice(!hasJapaneseVoice());
        check();
        const synth = window.speechSynthesis;
        synth.addEventListener?.("voiceschanged", check);
        return () => synth.removeEventListener?.("voiceschanged", check);
    }, [supported, needsJapanese, state]);

    // Report the spoken block so the caller can highlight it; cleared when idle or unmounted.
    const blockIndex = state === "idle" || !progress ? null : progress.blockIndex;
    useEffect(() => {
        onBlockChange?.(blockIndex);
    }, [blockIndex, onBlockChange]);
    useEffect(() => () => onBlockChange?.(null), [onBlockChange]);

    if (!supported) {
        return <p className={styles.note}>{t("post.readAloud.unsupported")}</p>;
    }

    const play = () => {
        setFailed(false);
        playerRef.current?.stop();
        playerRef.current = createReadAloudPlayer(segments, {
            onProgress: setProgress,
            onEnd: reset,
            onError: () => {
                reset();
                setFailed(true);
            },
            onPauseRejected: () => setState("playing"),
            rate,
        });
        if (slug) analytics.track("blog_audio_played", { slug });
        setState("playing");
        playerRef.current.play();
    };

    const stop = () => {
        playerRef.current?.stop();
        setFailed(false);
        reset();
    };

    // One primary button whose label and handler switch, so keyboard focus stays on it.
    let primaryLabel = t("post.readAloud.play");
    let primaryIcon = faPlay;
    let primaryAction = play;
    if (state === "playing") {
        primaryLabel = t("post.readAloud.pause");
        primaryIcon = faPause;
        primaryAction = () => {
            playerRef.current?.pause();
            setState("paused");
        };
    } else if (state === "paused") {
        primaryLabel = t("post.readAloud.resume");
        primaryAction = () => {
            playerRef.current?.resume();
            setState("playing");
        };
    }

    const heading = title ?? t("post.readAloud.title");
    let statusText = "";
    if (state === "playing") statusText = t("post.readAloud.playing");
    if (state === "paused") statusText = t("post.readAloud.paused");

    return (
        <div className={styles.controls} role="group" aria-label={heading}>
            <span className={styles.title}>
                <FontAwesomeIcon icon={faVolumeHigh} aria-hidden="true" />
                {heading}
            </span>
            <div className={styles.buttons}>
                <button
                    type="button"
                    className={styles.button}
                    onClick={primaryAction}
                    disabled={segments.length === 0}
                >
                    <FontAwesomeIcon icon={primaryIcon} aria-hidden="true" />
                    {primaryLabel}
                </button>
                {state !== "idle" && (
                    <button type="button" className={styles.button} onClick={stop}>
                        <FontAwesomeIcon icon={faStop} aria-hidden="true" />
                        {t("post.readAloud.stop")}
                    </button>
                )}
            </div>
            <p className={styles.status} aria-live="polite">
                {statusText}
            </p>
            {showProgress && state !== "idle" && progress && (
                <p className={styles.progress}>
                    {t("post.readAloud.progress", { current: progress.blockPosition, total: progress.blockTotal })}
                </p>
            )}
            {failed && (
                <p className={styles.status} role="alert">
                    {t("post.readAloud.error")}
                </p>
            )}
            {state !== "idle" && missingJapaneseVoice && (
                <p className={styles.note}>{t("post.readAloud.noJapaneseVoice")}</p>
            )}
        </div>
    );
}
